import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API } from '../App';
import { MapPin, Calendar, TrendingUp, Filter, ArrowLeft, Search, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AuthContext } from "../App";
import { Link } from "react-router-dom";

const ReportsList = () => {
    const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState(location.state?.category || 'all');
  const [filterStatus, setFilterStatus] = useState('fundraising');
  const [filterCity, setFilterCity] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [locationSearchActive, setLocationSearchActive] = useState(false);
  const [cities, setCities] = useState([]);
  const { user } = useContext(AuthContext) || {};
  const isStaff = !!user && ["moderator", "admin", "super_admin"].includes(user.role);


  const statusOptions = useMemo(() => {
    // BEFORE login
    if (!user) {
      return ["verified", "fundraising", "completed"]; // add "all" if you want
    }
    // AFTER login
    return ["all", "submitted", "verified", "fundraising", "in_progress", "completed"];
  }, [user]);

  // if user logs out while "submitted/in_progress" selected -> reset
  useEffect(() => {
    if (!statusOptions.includes(filterStatus)) {
      setFilterStatus("fundraising");
    }
  }, [statusOptions]); // eslint-disable-line
  
  useEffect(() => {
    fetchReports();
  }, [filterCategory, filterStatus, filterCity, locationSearchActive]);

  useEffect(() => {
    // Extract unique cities from reports
    const uniqueCities = [...new Set(reports.map(r => {
      const address = r.location.address;
      const parts = address.split(',');
      return parts[parts.length - 2]?.trim() || parts[parts.length - 1]?.trim();
    }))].filter(Boolean).sort();
    setCities(uniqueCities);
  }, [reports]);

  const fetchReports = async () => {
    try {
      const params = {};
      if (filterCategory && filterCategory !== 'all') params.category = filterCategory;
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      
      // Prioritize location search over city filter
      if (locationSearchActive && locationSearch.trim()) {
        params.location_search = locationSearch.trim();
      } else if (filterCity && filterCity !== 'all') {
        params.city = filterCity;
      }
      
      const res = await axios.get(`${API}/reports`, { params });
      setReports(res.data);
    } catch (err) {
      console.error('Failed to fetch reports', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSearch = () => {
    if (locationSearch.trim()) {
      setLocationSearchActive(true);
      setFilterCity('all'); // Clear city filter when using location search
    }
  };

  const clearLocationSearch = () => {
    setLocationSearch('');
    setLocationSearchActive(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getProgressPercentage = (report) => {
    if (!report.estimated_cost) return 0;
    return Math.min((report.collected_amount / report.estimated_cost) * 100, 100);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <button
            data-testid="back-home-btn"
            onClick={() => navigate('/')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            {t("nav.backHome")}
          </button>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t("reports.title")}</h1>
              <p className="text-gray-600 mt-1">{reports.length} {t("reports.issuesReported")}</p>
            </div>
            <Button
              data-testid="create-new-report-btn"
              onClick={() => navigate('/report')}
              className="btn-primary"
            >
              {t("reports.reportNew")}
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4 flex-wrap">
            <Filter size={20} className="text-gray-400" />
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger data-testid="filter-category" className="w-48">
                <SelectValue placeholder={t("filter.allCategories")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("filter.allCategories")}</SelectItem>
                <SelectItem value="pothole">{t("category.pothole")}</SelectItem>
                <SelectItem value="road">{t("category.road")}</SelectItem>
                <SelectItem value="water">{t("category.water")}</SelectItem>
                <SelectItem value="electricity">{t("category.electricity")}</SelectItem>
                <SelectItem value="garbage">{t("category.garbage")}</SelectItem>
                <SelectItem value="safety">{t("category.safety")}</SelectItem>
                <SelectItem value="general">{t("category.general")}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="filter-status" className="w-48">
                <SelectValue placeholder={t("filter.allStatus")} />
              </SelectTrigger>
              <SelectContent>
               {statusOptions.map((s) => (
                <SelectItem key={s} value={s}>
                 {s === "all" ? t("filter.allStatus") : t(`status.${s}`)}
                </SelectItem>
                ))}
             </SelectContent>
            </Select>
            <Select value={filterCity} onValueChange={setFilterCity} disabled={locationSearchActive}>
              <SelectTrigger data-testid="filter-city" className="w-48">
                <SelectValue placeholder={t("filter.allLocations")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                <SelectItem value="Mumbai">Mumbai</SelectItem>
                <SelectItem value="Delhi">Delhi</SelectItem>
                <SelectItem value="Bangalore">Bangalore</SelectItem>
                <SelectItem value="Hyderabad">Hyderabad</SelectItem>
                <SelectItem value="Chennai">Chennai</SelectItem>
                <SelectItem value="Kolkata">Kolkata</SelectItem>
                <SelectItem value="Pune">Pune</SelectItem>
                <SelectItem value="Ahmedabad">Ahmedabad</SelectItem>
                <SelectItem value="Jaipur">Jaipur</SelectItem>
                <SelectItem value="Lucknow">Lucknow</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Location Search */}
          <div className="mt-4">
            <div className="flex items-center gap-2 max-w-2xl">
              <div className="flex-1 relative">
                <Input
                  data-testid="location-search-input"
                  placeholder={t("filter.locationPlaceholder")}
                  value={locationSearch}
                  onChange={(e) => setLocationSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleLocationSearch()}
                  className="pr-10"
                />
                {locationSearchActive && (
                  <button
                    onClick={clearLocationSearch}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    data-testid="clear-location-search"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
              <Button
                data-testid="search-location-btn"
                onClick={handleLocationSearch}
                disabled={!locationSearch.trim()}
              >
                <Search size={18} className="mr-2" />
                {t("filter.searchLocation")}
              </Button>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              <p>
                {t("filter.locationHint")}
              </p>
              <ul className="ml-4 mt-1 space-y-1">
                <li>• <strong>{t("filter.coordinates")}:</strong> 28.6139, 77.2090</li>
                <li>• <strong>{t("filter.plusCode")}:</strong> J9Q6+97 Ghaziabad</li>
                <li>• <strong>{t("filter.fullPlusCode")}:</strong> 7JVW2JPJ+QQ</li>
              </ul>
              {locationSearchActive && (
                <p className="mt-2 text-cyan-600 font-medium">
                  📍 {t("filter.showingNear")}: {locationSearch}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reports Grid */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {loading ? (
          <div className="text-center py-12">{t("reports.loading")}</div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">{t("reports.noReports")}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reports.map((report) => (
              <Link
                key={report.id}
                to={`/reports/${report.id}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`report-card-${report.id}`}
                className="card p-6 cursor-pointer"
              >
                {report.photos.length > 0 && (
                  <img
                    src={report.photos[0]}
                    alt={report.title}
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                )}
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-lg text-gray-900 flex-1">{report.title}</h3>
                  <span className={`status-badge status-${report.status}`}>
                    {report.status}
                  </span>
                </div>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{report.description}</p>
                
                <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                  <div className="flex items-center flex-1">
                    <MapPin size={14} className="mr-1 flex-shrink-0" />
                    <span className="truncate">{report.location.address}</span>
                  </div>
                  <button type="button" onClick={(e) => { e.preventDefault(); 
                   e.stopPropagation();
                   window.open(
                   `https://www.google.com/maps?q=${report.location.lat},${report.location.lng}`,
                    "_blank",
                   "noopener,noreferrer"
                  );
                  }}
                 className="ml-2 text-cyan-600 hover:text-cyan-700 flex items-center gap-1 text-xs font-medium flex-shrink-0"
                data-testid={`map-link-${report.id}`}
                >
                {t("common.viewMap")}
                </button>
                </div>
                <div className="flex items-center text-sm text-gray-500 mb-4">
                  <Calendar size={14} className="mr-1" />
                  {formatDate(report.created_at)}
                </div>

                {report.estimated_cost && (
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">{t("common.progress")}</span>
                      <span className="font-semibold text-cyan-600">
                        ₹{report.collected_amount.toLocaleString()} / ₹{report.estimated_cost.toLocaleString()}
                      </span>
                    </div>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${getProgressPercentage(report)}%` }}
                      />
                    </div>
                  </div>
                )}

                {report.urgency === 'critical' && (
                  <div className="mt-3 flex items-center text-red-600 text-sm font-medium">
                    <TrendingUp size={14} className="mr-1" />
                    Critical Priority
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsList;